import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import 'moment/min/locales';
import { ADVERT_TYPE, PROPERTY_TYPE } from 'types';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export const formatPrice = (price?: number | null, exchangeRate?: number) => {
    if (!price) return '0';

    if (exchangeRate) {
        price *= exchangeRate;
        price = Math.round(price * 100) / 100;
    }

    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export function formatPhoneNumber(number?: string | null) {
    if (!number) return '';
    const phoneNumber = parsePhoneNumberFromString(number);
    if (phoneNumber && phoneNumber.isValid()) {
        return phoneNumber.formatInternational();
    }

    return number;
}

export const formatDate = ({
    inputDate,
    locale = 'az-AZ',
    format,
}: {
    inputDate: string;
    locale?: string;
    format?: string;
}) => {
    moment.locale(locale);
    const now = moment();
    const dateTime = moment.utc(inputDate).local();

    if (format) {
        return dateTime.format(format);
    }

    const labels = {
        today: {
            'az-AZ': 'Bu gün',
            'en-US': 'Today',
            'ru-RU': 'Сегодня',
        },
        yesterday: {
            'az-AZ': 'Dünən',
            'en-US': 'Yesterday',
            'ru-RU': 'Вчера',
        },
    };

    const todayLabel = labels.today[locale as keyof typeof labels['today']];
    const yesterdayLabel = labels.yesterday[locale as keyof typeof labels['yesterday']];

    if (dateTime.isSame(now, 'day')) {
        return `${todayLabel} ${dateTime.format('HH:mm')}`;
    } else if (dateTime.isSame(now.subtract(1, 'day'), 'day')) {
        return `${yesterdayLabel} ${dateTime.format('HH:mm')}`;
    } else {
        return dateTime.format('D MMM YYYY');
    }
};

export const getShowDocumentStatus = (advertType: ADVERT_TYPE) => advertType === ADVERT_TYPE.SALE;
export const getShowMortgageStatus = (advertType: ADVERT_TYPE) => advertType === ADVERT_TYPE.SALE;
export const getShowRoomCount = (propertyType: PROPERTY_TYPE, roomCount?: number) =>
    [
        PROPERTY_TYPE.NEW_BUILDING,
        PROPERTY_TYPE.OLD_BUILDING,
        PROPERTY_TYPE.YARD_HOUSE,
        PROPERTY_TYPE.COMMERCIAL,
    ].includes(propertyType) && !!roomCount;
export const getShowFloorInfo = (propertyType: PROPERTY_TYPE) =>
    [PROPERTY_TYPE.NEW_BUILDING, PROPERTY_TYPE.OLD_BUILDING, PROPERTY_TYPE.COMMERCIAL].includes(
        propertyType,
    );
export const getShowMaintenance = (propertyType: PROPERTY_TYPE) =>
    [
        PROPERTY_TYPE.NEW_BUILDING,
        PROPERTY_TYPE.OLD_BUILDING,
        PROPERTY_TYPE.OFFICE,
        PROPERTY_TYPE.YARD_HOUSE,
        PROPERTY_TYPE.COMMERCIAL,
    ].includes(propertyType);
export const getShowPropertyLandArea = (propertyType: PROPERTY_TYPE) =>
    [PROPERTY_TYPE.YARD_HOUSE].includes(propertyType);
export const isInvalidValue = (value: any) => {
    return value === undefined || value === null || Number.isNaN(value);
};
export const getArrayValue = <T>(array: T[]): T[] | undefined => (array.length ? array : undefined);
